/**
 * TwoFactorNudge — dismissible banner reminding admins/super-admins to enable 2FA.
 *
 * Shown only when:
 *   • user.role is admin, super_admin, billing_admin, or supervisor
 *   • user.totp_enabled is falsy
 *   • the user hasn't dismissed it in the last 7 days
 *   • not on login/portal pages
 *
 * Non-blocking: never intercepts navigation.  A single close button snoozes
 * the nudge for 7 days via localStorage.
 */
import React, { useState, useEffect } from 'react';
import { ShieldAlert, X } from 'lucide-react';

const SNOOZE_KEY = 'cc.twoFactorNudge.snoozedUntil';
const SNOOZE_DAYS = 7;

const isHighPrivRole = (role) =>
  ['admin', 'super_admin', 'billing_admin', 'supervisor'].includes(String(role || '').toLowerCase());

export default function TwoFactorNudge({ user, onEnable }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || !isHighPrivRole(user.role)) return;
    if (user.totp_enabled) return;
    try {
      const snoozed = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10);
      if (snoozed && snoozed > Date.now()) return;
    } catch { /* localStorage unavailable */ }
    setVisible(true);
  }, [user]);

  const dismiss = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400 * 1000));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="mx-4 my-2 flex items-center gap-3 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 px-4 py-2.5 shadow-sm"
      data-testid="two-factor-nudge"
    >
      <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-900">
          Add two-factor authentication
        </div>
        <div className="text-xs text-amber-800 leading-snug">
          Your role has elevated access. Turning on 2FA takes ~60 seconds and keeps PHI safe if a password ever leaks.
        </div>
      </div>
      <button
        onClick={onEnable}
        className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold whitespace-nowrap transition-colors"
        data-testid="two-factor-nudge-enable"
      >
        Enable 2FA
      </button>
      <button
        onClick={dismiss}
        className="p-1 rounded-lg text-amber-700 hover:bg-amber-100"
        data-testid="two-factor-nudge-dismiss"
        aria-label="Dismiss for a week"
        title="Remind me next week"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
